/** 달리기 중 정지로 받아들이기까지 정지 판정이 이어져야 하는 시간(영웅 초). 60Hz 한 프레임(16.7ms)보다 길고 두 프레임보다 짧다. */
const STOP_HOLD = 0.03;

/** 이동거리 → 제자리 달리기 위상. 충돌 후 보간된 위치만 읽고 게임 좌표·시계는 바꾸지 않는다. */
export class DistanceGait {
  time = 0;
  speed = 0;
  moving = false;
  /** 한 바퀴 안 진행 비율(0..1, 거리 기준). 걷기 층은 이 비율로 같은 디딤 위상을 쓴다. */
  fraction = 0;
  private distance = 0;
  private x = 0;
  private z = 0;
  private initialized = false;
  /** 정지 판정이 이어진 시간(영웅 초). STOP_HOLD보다 짧으면 달리기를 유지한다. */
  private still = 0;

  reset(): void {
    this.time = this.speed = this.distance = this.still = this.fraction = 0;
    this.moving = this.initialized = false;
  }

  /**
   * stride: 이동거리 배율(기본 1). 걷기 섞기에서 한 바퀴 거리가 달리기보다 짧으면 1보다 커져
   * 같은 이동거리에 위상이 더 빨리 돈다(디딤 발이 땅에 붙은 채 짧은 보폭으로 걷는다).
   */
  update(x: number, z: number, dt: number, active: boolean, maxSpeed: number, duration: number, cycle: Float32Array, stride = 1): void {
    const distance = this.initialized ? Math.hypot(x - this.x, z - this.z) : 0;
    this.x = x; this.z = z; this.initialized = true;
    // 히트스톱에는 자세·보행 여부도 고정한다. 위치 이력은 갱신하여 재개 때 밀린 거리를 소비하지 않는다.
    if (dt <= 0) return;
    this.speed = distance / dt;
    // 장면 스냅·공격 돌진은 달리기 거리로 세지 않는다.
    if (!active || this.speed > maxSpeed * 1.5) { this.moving = false; this.still = 0; return; }
    // 달리다 반대로 꺾는 틱에는 보간 위치가 앞 프레임과 같아(60Hz, 앞으로 반 틱·뒤로 반 틱) 속도가 0으로 읽힌다.
    // 한 프레임만 정지로 바뀌면 대기↔달리기 섞기가 연달아 시작돼 다리가 한 프레임에 0.69m 튀었다(2026-10-20, 180도 회전 41m/s).
    // 정지는 STOP_HOLD 동안 이어질 때만 받아들인다(벽에 막힌 정지는 그만큼 늦게 대기로 간다).
    if (this.speed > (this.moving ? 0.5 : 0.55)) { this.moving = true; this.still = 0; }
    else if (this.moving) { this.still += dt; if (this.still >= STOP_HOLD) this.moving = false; }
    if (cycle.length < 2) return;
    const total = cycle[cycle.length - 1];
    if (total <= 0) return;
    this.distance = (this.distance + distance * stride) % total;
    this.fraction = this.distance / total;
    let lo = 0, hi = cycle.length - 1;
    while (hi - lo > 1) {
      const mid = (lo + hi) >> 1;
      if (cycle[mid] <= this.distance) lo = mid;
      else hi = mid;
    }
    const u = (this.distance - cycle[lo]) / Math.max(1e-8, cycle[hi] - cycle[lo]);
    this.time = (lo + u) * duration / (cycle.length - 1);
  }
}

/**
 * 달리기 위상표의 두 반주기(왼발 디딤 시작→오른발 디딤 시작, 오른발→왼발) 진행 거리를 같게 맞춘다.
 * 원본 클립의 보폭이 좌우로 약 10% 달라 같은 속도에서 디딤 간격이 좌우로 달랐다(2026-10-19 측정 0.267s 대 0.233s).
 * 디딤 표본(stanceL/stanceR)의 거리는 그 발의 실제 뒤로 쓸리는 속도라 손대지 않고(미끄럼 0 설계 유지),
 * 두 발이 모두 떠 있는 체공 표본의 거리만 반주기별로 고르게 다시 나눈다. step[i]는 표본 i→i+1 진행 거리(m).
 * 반주기를 찾지 못하거나 체공 거리가 음수가 되면 바꾸지 않고 false를 돌려준다.
 */
export function balanceHalfCycles(step: Float32Array, stanceL: Uint8Array, stanceR: Uint8Array): boolean {
  const n = step.length;
  if (n < 4 || stanceL.length !== n || stanceR.length !== n) return false;
  const onset = (s: Uint8Array): number => {
    for (let i = 0; i < n; i++) if (s[i] && !s[(i + n - 1) % n]) return i;
    return -1;
  };
  const l = onset(stanceL), r = onset(stanceR);
  if (l < 0 || r < 0 || l === r) return false;
  let total = 0;
  for (let i = 0; i < n; i++) total += step[i];
  const half = total / 2;
  const plan: { from: number; flight: number; fixed: number }[] = [];
  for (const [a, b] of [[l, r], [r, l]] as const) {
    let fixed = 0, flight = 0;
    for (let k = 0, i = a; i !== b; k++, i = (i + 1) % n) {
      if (stanceL[i] || stanceR[i]) fixed += step[i]; else flight++;
      if (k > n) return false;
    }
    if (!flight || half - fixed <= 0) return false;
    plan.push({ from: a, flight, fixed });
  }
  for (let h = 0; h < 2; h++) {
    const { from, flight, fixed } = plan[h], to = h ? l : r, each = (half - fixed) / flight;
    for (let i = from; i !== to; i = (i + 1) % n) if (!stanceL[i] && !stanceR[i]) step[i] = each;
  }
  return true;
}
